import React from "react";
import useUser from "./useUser";
import useIsTeam from "./useIsTeam";
import { LogData } from "@/decleration";
import { useQuery } from "@supabase-cache-helpers/postgrest-swr";
import { createClient } from "@supabase/supabase-js";
import config from "@/config";

type LogType = {
  content: string;
  logId: string;
  timeStamp: string;
  userId: string;
};

const client = createClient(config.SUPABASE_URL, config.SUPABASE_KEY);

const TABLE_NAME = config.SUPABASE_TABLE_NAME;

const useLogs = (logData?: LogData) => {
  const { user } = useUser();
  const { id: teamId, isTeam } = useIsTeam();
  const [isPaused, setIsPaused] = React.useState(false);
  const [cachedLogs, setCachedLogs] = React.useState<any[]>([]);
  const [pendingLogs, setPendingLogs] = React.useState<any[]>([]);
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);

  const {
    data: logs,
    error,
    mutate,
  } = useQuery(
    client
      .from(TABLE_NAME)
      .select("*")
      .eq("logId", logData?.id || "")
      .eq("ownerId", isTeam ? teamId : user?.id || "")
      .order("timestamp", { ascending: true })
      .limit(1000),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess: (data: any) => {
        if (data) {
          setCachedLogs(data);
          setInitialLoadComplete(true);
        }
      },
    },
  );

  // Store the initial logs when fetched
  React.useEffect(() => {
    if (logs) {
      setCachedLogs(logs);
    }
  }, [logs]);

  React.useEffect(() => {
    if (!logData?.id || !user?.id) return;

    const fetchAllLogs = async () => {
      try {
        const response = await client
          .from(TABLE_NAME)
          .select("*")
          .eq("logId", logData.id)
          .eq("ownerId", isTeam ? teamId : user?.id)
          .order("timestamp", { ascending: true });

        if (response.error) {
          console.error("Error fetching complete logs:", response.error);
          return;
        }

        if (response.data && response.data.length > 0) {
          mutate(response, false);
        }
      } catch (err) {
        console.error("Failed to fetch complete logs:", err);
      }
    };

    fetchAllLogs();
  }, [logData?.id, user?.id, teamId, isTeam, mutate]);

  const togglePause = React.useCallback(() => {
    setIsPaused((prev) => {
      if (prev) {
        mutate((currentLogs: any) => {
          if (!currentLogs || !currentLogs.data) return currentLogs;

          const updatedData = [...currentLogs.data, ...pendingLogs];

          return {
            ...currentLogs,
            data: updatedData,
          };
        }, false);

        setPendingLogs([]);
      }
      return !prev;
    });
  }, [pendingLogs, mutate]);

  // Subscribe to new logs
  React.useEffect(() => {
    if (!client || typeof window === "undefined" || !logData?.id) {
      return;
    }

    const subscription = client
      .channel(TABLE_NAME)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: TABLE_NAME },
        (payload) => {
          const newLog: LogType = payload.new as any;
          if (newLog.logId === logData?.id) {
            if (isPaused) {
              // If paused, add to pending logs
              setPendingLogs((prev) => [...prev, newLog]);
            } else {
              // If not paused, update the logs immediately
              mutate((currentLogs: any) => {
                if (!currentLogs || !currentLogs.data) return currentLogs;

                const updatedData = [...currentLogs.data, newLog];

                return {
                  ...currentLogs,
                  data: updatedData,
                };
              }, false);
            }
          }
        },
      )
      .subscribe();

    return () => {
      subscription?.unsubscribe();
    };
  }, [logData?.id, teamId, user?.id, isTeam, client, mutate, isPaused]);

  return {
    logs: logs || [],
    loading: !error && !logs,
    error,
    isPaused,
    togglePause,
    pendingLogsCount: pendingLogs.length,
  };
};

export default useLogs;
